import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Autocomplete,
  Fab,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CommunityCard from '../Layout/CommunityCard';
import ArticleModal from '../Layout/ArticleModal';
import { get, put, post } from '../../utility/fetchWeb';
import notification from '../../utility/notification';
import { hasPrivilege, PRIVILEGES } from '../../utils/privilegeHelper';

const ArticlePage = () => {
  const [communityPosts, setCommunityPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasMoreArticles, setHasMoreArticles] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const debounceTimer = useRef(null);

  const getArticles = useCallback(
    async (page = 1, categoryId = '', search = '', append = true) => {
      if (loadingRef.current) return;
      setLoadingArticles(true);
      loadingRef.current = true;

      try {
        const params = new URLSearchParams({
          page,
          pageSize,
          featuredOnly: 'true'
        });

        if (categoryId) params.append('categoryId', categoryId);
        if (search) params.append('search', search);

        const response = await get(`/Articles?${params.toString()}`);

        if (response?.code === 200 && response?.data) {
          const articles = response.data.map(item => ({
            imageUrl: item?.featuredImagePath,
            id: item?.id,
            title: item?.title,
            subtitle: item?.fullDescription,
            description: item?.fullDescription,
            author: item?.author || "Community",
            time: new Date(item?.publishedDate)?.toLocaleDateString(),
          }));

          setCommunityPosts(prevPosts => append ? [...prevPosts, ...articles] : articles);
          setHasMoreArticles(articles.length === pageSize);
        } else {
          setHasMoreArticles(false);
        }
      } catch (error) {
        console.error('Error fetching articles:', error);
        setHasMoreArticles(false);
      } finally {
        setLoadingArticles(false);
        loadingRef.current = false;
      }
    },
    [pageSize]
  );

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
    setCommunityPosts([]);
    setHasMoreArticles(true);
    getArticles(1, categoryId, searchQuery, false);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setCurrentPage(1);
      setCommunityPosts([]);
      setHasMoreArticles(true);
      getArticles(1, selectedCategoryId, query, false);
    }, 500);
  };

  const clearFilters = () => {
    setSelectedCategoryId('');
    setSearchQuery('');
    setCurrentPage(1);
    setCommunityPosts([]);
    setHasMoreArticles(true);
    getArticles(1, '', '', false);
  };

  const handleArticleSubmit = async (data) => {
    const isEdit = editingArticle !== null;
    setIsUpdating(true);

    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    formData.append('UserId', sessionStorage.getItem('userId'));

    try {
      const res = isEdit
        ? await put(`/Articles/${editingArticle.id}`, formData)
        : await post('/articles', formData);

      if (res.code === 200) {
        notification({
          message: `Article ${isEdit ? 'updated' : 'created'} successfully`,
          type: 'success'
        });
        setIsArticleModalOpen(false);
        clearFilters();
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      notification({
        message: error.message || 'Failed to save article',
        type: 'error'
      });
    } finally {
      setIsUpdating(false);
      setEditingArticle(null);
    }
  };

  const handleArticleEdit = (articleId) => {
    const article = communityPosts.find(post => post.id === articleId);
    if (article) {
      setEditingArticle(article);
      setIsArticleModalOpen(true);
    }
  };

  const handleArticleDelete = async (articleId) => {
    try {
      const res = await fetch(`/Articles/${articleId}`, { method: 'DELETE' });
      if (res.ok) {
        setCommunityPosts(prev => prev.filter(post => post.id !== articleId));
        notification({ message: 'Article deleted successfully', type: 'success' });
      } else {
        throw new Error('Failed to delete article');
      }
    } catch (error) {
      notification({ message: error.message, type: 'error' });
    }
  };

  useEffect(() => {
    getArticles(1, '', '', false);
    // Fetch categories and prepend "All" option
    get('/categories').then(response => {
      if (response?.code === 200) {
        const allOption = { id: '', name: 'All' };
        setCategories([allOption, ...response.data]);
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (first.isIntersecting && hasMoreArticles && !loadingRef.current) {
          setCurrentPage(prev => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => observer.disconnect();
  }, [hasMoreArticles]);

  useEffect(() => {
    if (currentPage > 1) {
      getArticles(currentPage, selectedCategoryId, searchQuery, true);
    }
  }, [currentPage, getArticles, selectedCategoryId, searchQuery]);

  // Add privilege checking
  const canCreateArticle = hasPrivilege(PRIVILEGES.CREATE_ARTICLE);
  const adminAccount = parseInt(sessionStorage.getItem('userId'), 10);

  return (
    <div className='w-100'>
      <Box sx={{ p: 3, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Articles
          </Typography>
          
          {/* Create Article Button - Only show if user has privilege */}
          {canCreateArticle && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingArticle(null);
                setIsArticleModalOpen(true);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                display: { xs: 'none', sm: 'flex' } // Hide on mobile, show FAB instead
              }}
            >
              Create Article
            </Button>
          )}
        </Box>

        {/* Search and filters section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
              {/* Category Filter with "All" option */}
              <Autocomplete
                value={categories.find(cat => cat.id === selectedCategoryId) || categories[0] || null}
                onChange={(event, newValue) => {
                  // If "All" is selected (id is empty string), pass empty string to fetch all
                  handleCategoryChange(newValue ? newValue.id : '');
                }}
                options={categories}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category Filter"
                    placeholder="Select category..."
                    sx={{ minWidth: 200 }}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                sx={{ minWidth: 200, flex: 1 }}
              />

              {/* Search Input */}
              <TextField
                label="Search Articles"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by title..."
                sx={{ minWidth: 200, flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* Clear Filters Button */}
            <Button 
              variant="outlined" 
              onClick={clearFilters}
              sx={{ 
                height: 'fit-content',
                whiteSpace: 'nowrap'
              }}
            >
              Clear Filters
            </Button>
          </Box>

          {/* Results count with category indicator */}
          <Typography variant="body2" color="text.secondary">
            {communityPosts.length} article{communityPosts.length !== 1 ? 's' : ''} found
            {selectedCategoryId && categories.length > 0 && (
              <span>
                {' '}in <strong>{categories.find(cat => cat.id === selectedCategoryId)?.name}</strong>
              </span>
            )}
            {!selectedCategoryId && categories.length > 0 && (
              <span> in <strong>All Categories</strong></span>
            )}
          </Typography>
        </Box>

        {/* Articles grid */}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {communityPosts.map((article, index) => (
            <CommunityCard
              key={`article-${article.id}-${index}`}
              {...article}
              onEdit={canCreateArticle ? () => handleArticleEdit(article.id) : undefined}
              onDelete={canCreateArticle ? () => handleArticleDelete(article.id) : undefined}
            />
          ))}
        </Box>

        {/* Loading and end of content indicators */}
        {loadingArticles && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Sentinel for infinite scroll */}
        <div ref={sentinelRef} style={{ height: '20px' }} />

        {/* Floating Action Button for mobile - Only show if user has privilege */}
        {canCreateArticle || adminAccount == 235 && (
          <Tooltip title="Create Article" placement="left">
            <Fab
              color="primary"
              aria-label="create article"
              onClick={() => {
                setEditingArticle(null);
                setIsArticleModalOpen(true);
              }}
              sx={{
                position: 'fixed',
                bottom: 24,
                right: 24,
                display: { xs: 'flex', sm: 'none' }, // Only show on mobile
                zIndex: 1000
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}

        {/* Article Modal */}
        <ArticleModal
          open={isArticleModalOpen}
          onClose={() => {
            setIsArticleModalOpen(false);
            setEditingArticle(null);
          }}
          editingArticle={editingArticle}
          onSubmit={handleArticleSubmit}
        />
      </Box>
    </div>
  );
};

export default ArticlePage;
